import React from "react";
import { useParams } from "react-router";
import cn from "classnames";
import { useGetBlogCurrentPostQuery } from "src/redux/api";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { Loader } from "src/components/Loader";
import { Sizes } from "src/@types/sizes";
import { formatDate } from "src/utils/formatDate";
import { getImageUrl } from "src/constants/urls";
import { Link } from "src/components/Link";
import { PATHNAMES } from "src/constants/routes";
import { Theme } from "src/@types/blog";
import { SEO } from "src/components/SEO/SEO";
import { SafeHTML } from "src/components/SafeHTML";

const BlogPost = () => {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useGetBlogCurrentPostQuery(id || "");

  if (!id) {
    return (
      <>
        <SEO
          title="Помилка"
          description="Статтю не знайдено"
          url={PATHNAMES.BLOG}
          noindex
        />
        <PageWrapper>
        <section className="pb-6 container">
          <div className="flex flex-col gap-5 lg:gap-8 p-5 lg:p-10 2xl:py-15 2xl:px-31 bg-white-base rounded-2xl">
            <h2 className="text-28 sm:text-4xl lg:text-5xl 2xl:text-64 text-green-dark">
              Помилка
            </h2>
            <p className="text-green-dark">ID статті не вказано</p>
            <Link href={PATHNAMES.BLOG} className="text-green-base hover:underline">
              ← Повернутися до блогу
            </Link>
          </div>
        </section>
      </PageWrapper>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <SEO
          title="Завантаження..."
          url={PATHNAMES.BLOG_ITEM(id)}
          noindex
        />
        <PageWrapper>
        <section className="pb-6 container">
          <div className="flex flex-col gap-5 lg:gap-8 p-5 lg:p-10 2xl:py-15 2xl:px-31 bg-white-base rounded-2xl">
            <Loader size={Sizes.L} />
          </div>
        </section>
      </PageWrapper>
      </>
    );
  }

  if (isError || !data) {
    return (
      <>
        <SEO
          title="Помилка завантаження"
          description="Статтю не знайдено або сталася помилка"
          url={PATHNAMES.BLOG}
          noindex
        />
        <PageWrapper>
        <section className="pb-6 container">
          <div className="flex flex-col gap-5 lg:gap-8 p-5 lg:p-10 2xl:py-15 2xl:px-31 bg-white-base rounded-2xl">
            <h2 className="text-28 sm:text-4xl lg:text-5xl 2xl:text-64 text-green-dark">
              Помилка завантаження
            </h2>
            <p className="text-green-dark">Статтю не знайдено або сталася помилка</p>
            <Link href={PATHNAMES.BLOG} className="text-green-base hover:underline">
              ← Повернутися до блогу
            </Link>
          </div>
        </section>
      </PageWrapper>
      </>
    );
  }

  const formattedDate = formatDate(data.date);
  const imageUrl = data.background_image ? getImageUrl(data.background_image) : null;
  const isDarkTheme = data.theme === Theme.DARK;

  // Extract plain text from HTML description for meta description
  const plainDescription = data.description
    ? data.description.replace(/<[^>]*>/g, "").substring(0, 160)
    : "";

  return (
    <>
      <SEO
        title={data.title}
        description={plainDescription || `Читайте статтю "${data.title}" в нашому блозі`}
        url={PATHNAMES.BLOG_ITEM(id)}
        image={imageUrl || undefined}
        type="article"
      />
      <PageWrapper>
      <article className="pb-6 container">
        {imageUrl && (
          <div className="relative w-full h-64 md:h-80 lg:h-96 xl:h-[500px] mb-6 rounded-2xl overflow-hidden">
            <img
              src={imageUrl}
              alt={data.title}
              className="w-full h-full object-cover object-center"
            />
            <div
              className={cn(
                "absolute inset-0 bg-gradient-to-t from-black/60 to-transparent",
                {
                  "from-green-dark/60": isDarkTheme,
                }
              )}
            />
          </div>
        )}

        <div
          className={cn(
            "flex flex-col gap-5 lg:gap-8 p-5 lg:p-10 2xl:py-15 2xl:px-31 rounded-2xl",
            {
              "bg-white-base": !isDarkTheme,
              "bg-green-dark": isDarkTheme,
            }
          )}
          >
          <Link
            href={PATHNAMES.BLOG}
            className={cn(
              "inline-flex items-center gap-2 text-sm lg:text-base hover:underline self-start",
              {
                "text-green-dark": !isDarkTheme,
                "text-white-base": isDarkTheme,
              }
            )}
          >
            ← Повернутися до блогу
          </Link>

          {formattedDate && (
            <time
              className={cn("text-xs lg:text-sm", {
                "text-green-dark": !isDarkTheme,
                "text-white-base": isDarkTheme,
              })}
              dateTime={data.date}
            >
              {formattedDate}
            </time>
          )}

          <h1
            className={cn(
              "text-28 sm:text-4xl lg:text-5xl 2xl:text-64 font-bold",
              {
                "text-green-dark": !isDarkTheme,
                "text-white-base": isDarkTheme,
              }
            )}
          >
            {data.title}
          </h1>

          <SafeHTML
            html={data.description}
            className={cn(
              "flex flex-col gap-3 text-xs xs:text-sm lg:text-base line-height [&_p]:mb-3 [&_strong]:font-bold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-2 [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_h4]:text-base",
              {
                "text-green-dark [&_h1]:text-green-dark [&_h2]:text-green-dark [&_h3]:text-green-dark [&_h4]:text-green-dark":
                  !isDarkTheme,
                "text-white-base [&_h1]:text-white-base [&_h2]:text-white-base [&_h3]:text-white-base [&_h4]:text-white-base":
                  isDarkTheme,
              }
            )}
          />
        </div>
      </article>
    </PageWrapper>
    </>
  );
};

export default BlogPost;
